import { eventChannel, END } from 'redux-saga'

import ReconnectingWebSocket from 'reconnecting-websocket'
import { CloseEvent } from 'reconnecting-websocket/events'

import { SOCKET_BASE_URL } from 'common/api/connectionConsts'
import { SocketActionTypes } from './types'

export const PING_TIMEOUT_MS = 45000
const websocketPingTimeoutError = `No PING or message received within ${
  PING_TIMEOUT_MS / 1000
} seconds`

/**
 * How a connection gets built. Production always uses ReconnectingWebSocket; tests inject a
 * double so the lifecycle can be exercised without a browser or a server.
 */
export type SocketFactory = (url: string) => ReconnectingWebSocket

const defaultSocketFactory: SocketFactory = (url) =>
  new ReconnectingWebSocket(url, [], { connectionTimeout: 4000 })

export let ws: ReconnectingWebSocket

export function createEventServer(
  instanceId: string,
  socketFactory: SocketFactory = defaultSocketFactory
) {
  return eventChannel((emit) => {
    // Both of these are per-connection, never read off the shared `ws`. Opening a new app
    // starts a second websocket while takeLatest tears the first one down, so for a moment
    // two connections are live and `ws` already points at the newer one.
    let socket: ReconnectingWebSocket
    let pingTimeout: NodeJS.Timeout | undefined

    // If no "PING" message is received within PING_TIMEOUT_MS seconds from the last message
    // received on THIS connection, it closes and reconnects it. This exists because a network
    // problem could otherwise leave the socket dead for ten minutes, with a page refresh as
    // the only way out.
    const setupPingTimeout = () => {
      clearTimeout(pingTimeout)
      pingTimeout = setTimeout(() => {
        console.error(
          `WebSocket error: ${websocketPingTimeoutError}. Closing WebSocket and trying to reconnect...`
        )
        socket.close(4001, websocketPingTimeoutError)
        socket.reconnect()
      }, PING_TIMEOUT_MS)
    }

    const createWs = () => {
      // Subscribe to websocket
      socket = socketFactory(`${SOCKET_BASE_URL}/${instanceId}`)
      ws = socket
      window.ppWebSocket = ws // Store ws in window object

      socket.addEventListener('open', () => {
        emit({ type: SocketActionTypes.WEBSOCKET_CONNECTION_STATUS_ON })
        setupPingTimeout()
      })

      socket.addEventListener('message', (event: MessageEvent) => {
        const message = event.data
        clearTimeout(pingTimeout)
        setupPingTimeout()
        if (message !== 'PING') {
          const parsed = JSON.parse(message)
          // payloadChars lets the interface cache budget the memory its results
          // take without walking (or re-serializing) the parsed payload. Only
          // object frames can carry it; anything else is passed through as is.
          emit({
            data:
              parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                ? { ...parsed, payloadChars: message.length }
                : parsed
          })
        } else {
          socket.send('PONG')
        }
      })

      socket.addEventListener('error', (error) => {
        console.log('WebSocket error: ', error)
      })

      socket.addEventListener('close', (event: CloseEvent) => {
        emit({ type: SocketActionTypes.WEBSOCKET_CONNECTION_STATUS_OFF })
        clearTimeout(pingTimeout)
        if (event.code === 1005) {
          emit(END)
        }
      })
    }

    createWs()

    return () => {
      clearTimeout(pingTimeout)
      socket.close()
    }
  })
}
